import { useState } from "react";

const SITE = "https://thelivephotos.com";
const CAPTION = "Created with thelivephotos.com";

type Props = {
  jobId: string;
  videoUrl: string | null;
};

export function ShareButtons({ jobId, videoUrl }: Props) {
  const [toast, setToast] = useState<string | null>(null);
  const shareUrl = `${SITE}/share/${jobId}`;
  const captionWithUrl = `${CAPTION} ${shareUrl}`;

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); flash("caption copied"); } catch {}
  };

  const openCentered = (url: string) => {
    const w = 640, h = 640;
    const top = window.screen.height / 2 - h / 2;
    const left = window.screen.width / 2 - w / 2;
    window.open(url, "share", `width=${w},height=${h},top=${top},left=${left},noopener,noreferrer`);
  };

  const fbShare = async () => {
    // FB ignores the &quote= prefill; copy the caption so the user can paste it.
    await copy(CAPTION);
    openCentered(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  };

  const twShare = () => {
    openCentered(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(CAPTION)}&url=${encodeURIComponent(shareUrl)}`
    );
  };

  const waShare = () => {
    // wa.me opens WhatsApp Web on desktop, the app on mobile.
    window.open(`https://wa.me/?text=${encodeURIComponent(captionWithUrl)}`, "_blank", "noopener,noreferrer");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "thelivephotos", text: CAPTION, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      await copy(captionWithUrl);
      flash("link copied — paste anywhere");
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 px-1 relative">
      {videoUrl && (
        <a
          href={videoUrl}
          download="livephoto.mp4"
          className="iconbtn"
          title="download"
        >
          <DownloadIcon /> <span className="sr-only">download</span>
        </a>
      )}
      <button onClick={fbShare} className="iconbtn iconbtn-fb" title="share to facebook" aria-label="share to facebook">
        <FbIcon />
      </button>
      <button onClick={twShare} className="iconbtn iconbtn-x" title="share to x / twitter" aria-label="share to twitter">
        <XIcon />
      </button>
      <button onClick={waShare} className="iconbtn iconbtn-wa" title="share to whatsapp" aria-label="share to whatsapp">
        <WaIcon />
      </button>
      <button onClick={nativeShare} className="iconbtn" title="more (instagram, mail…)" aria-label="more share options">
        <ShareIcon />
      </button>

      {toast && (
        <span className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-ink text-cream px-2.5 py-1 rounded-full shadow">
          {toast}
        </span>
      )}
    </div>
  );
}

function DownloadIcon() { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>); }
function FbIcon()      { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.06C22 6.48 17.52 2 12 2S2 6.48 2 12.06c0 4.97 3.66 9.1 8.44 9.88v-6.99H7.9v-2.89h2.54V9.83c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.89h-2.34V22c4.78-.78 8.45-4.91 8.45-9.94z"/></svg>); }
function XIcon()       { return (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2H21.5l-7.36 8.41L23 22h-6.78l-5.31-6.94L4.8 22H1.54l7.86-8.98L1 2h6.94l4.79 6.34L18.244 2zm-1.19 18h1.83L7.04 4H5.1l11.95 16z"/></svg>); }
function WaIcon()      { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.05 4.91A10 10 0 0 0 4.66 19.4L4 22l2.66-.7A10 10 0 0 0 19.05 4.92zM12 20a8 8 0 0 1-4.07-1.11l-.29-.17-2.64.7.71-2.57-.19-.3A8 8 0 1 1 12 20zm4.5-5.95c-.25-.13-1.46-.72-1.69-.8-.23-.08-.39-.13-.55.13s-.63.8-.78.96c-.14.16-.29.18-.54.06a6.62 6.62 0 0 1-1.95-1.21 7.38 7.38 0 0 1-1.36-1.7c-.14-.24 0-.37.1-.49.1-.1.23-.27.34-.4.12-.14.16-.24.24-.4a.45.45 0 0 0 0-.42c-.06-.13-.55-1.34-.76-1.84-.2-.48-.4-.41-.55-.42h-.47a.91.91 0 0 0-.66.31 2.78 2.78 0 0 0-.86 2.07A4.83 4.83 0 0 0 8 12.55a11 11 0 0 0 4.21 3.71 14.2 14.2 0 0 0 1.4.52 3.36 3.36 0 0 0 1.55.1 2.54 2.54 0 0 0 1.66-1.17 2.06 2.06 0 0 0 .14-1.17c-.06-.1-.22-.16-.46-.29z"/></svg>); }
function ShareIcon()   { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>); }
